import { api } from '../../lib/api';
import type { PagedResult } from '../administration/users/userTypes';
import type {
  DisbursementRequestDetail,
  DisbursementRequestListItem,
  DisbursementRequestListQuery,
  DisbursementRequestRequest,
} from './disbursementRequestTypes';

function toQueryString(query: DisbursementRequestListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  // Each facet is sent whenever its key is present, empty string included — "ticked to
  // nothing" matches no row, where an absent key is no constraint at all.
  if (query.status !== undefined) params.set('status', query.status);
  if (query.stage !== undefined) params.set('stage', query.stage);
  if (query.transactionType !== undefined) params.set('transactionType', query.transactionType);
  if (query.paymentType !== undefined) params.set('paymentType', query.paymentType);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

const BASE = '/api/disbursement-requests';

export const disbursementRequestsApi = {
  list: (query: DisbursementRequestListQuery, signal?: AbortSignal) =>
    api<PagedResult<DisbursementRequestListItem>>(`${BASE}${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<DisbursementRequestDetail>(`${BASE}/${id}`, { signal }),

  create: (body: DisbursementRequestRequest) =>
    api<DisbursementRequestDetail>(BASE, { method: 'POST', body }),

  update: (id: string, body: DisbursementRequestRequest) =>
    api<DisbursementRequestDetail>(`${BASE}/${id}`, { method: 'PUT', body }),

  /** Cash validates it and passes it to Accounting. */
  process: (id: string) => api<DisbursementRequestDetail>(`${BASE}/${id}/process`, { method: 'POST' }),

  /** The voucher is back from Accounting. */
  /** The caller's own employee record — what the voucher form's Received by defaults to. */
  me: (signal?: AbortSignal) =>
    api<{ personnelId?: string | null; employeeNumber?: string | null; error?: string | null }>(`${BASE}/me`, {
      signal,
    }),

  receiveVoucher: (
    id: string,
    body: { voucherNumber: string; voucherDate: string; receivedByPersonnelId: string | null },
  ) =>
    api<DisbursementRequestDetail>(`${BASE}/${id}/receive-voucher`, { method: 'POST', body }),

  /** Paid out to the requesting party. */
  release: (id: string, body: { disbursedOn: string; reference: string }) =>
    api<DisbursementRequestDetail>(`${BASE}/${id}/release`, { method: 'POST', body }),

  /** Sent back with a reason, while with Cash or at Accounting. */
  returnRequest: (id: string, reason: string) =>
    api<DisbursementRequestDetail>(`${BASE}/${id}/return`, { method: 'POST', body: { reason } }),

  remove: (id: string) => api<void>(`${BASE}/${id}`, { method: 'DELETE' }),

  restore: (id: string) => api<void>(`${BASE}/${id}/restore`, { method: 'POST' }),
};
